import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { getPartners } from '../services/firestoreService';
import { defaultPartners } from '../data/defaultPartners';
import { Partner, PartnerCategory } from '../types';
import { Card, SectionHeader } from './ui';

const categoryLabel: Record<PartnerCategory, string> = {
  institution: 'Institution',
  fondation: 'Fondation',
  commerce: 'Commerce',
};

const partnerOffers = [
  { title: 'Dons en nature', text: 'Repas, invendus, produits d\'hygiène ou vêtements pour nos distributions.' },
  { title: 'Soutien financier', text: 'Un don ponctuel ou régulier, avec reçu fiscal.' },
  { title: 'Visibilité', text: 'Votre logo sur notre site et vos actions relayées sur nos réseaux.' },
];

const PartnerLogo = ({ partner }: { partner: Partner }) => {
  const [failed, setFailed] = useState(false);

  if (!partner.logo || failed) {
    // Name card when no logo is available. The logo box stays white in both themes, hence fixed colours.
    return (
      <span className="heading text-base sm:text-lg text-center text-gray-800 group-hover:text-[#B4463C] transition-colors">
        {partner.name}
      </span>
    );
  }

  return (
    <img
      src={partner.logo}
      alt={partner.name}
      loading="lazy"
      onError={() => setFailed(true)}
      className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
    />
  );
};

const PartnerTile = ({ partner }: { partner: Partner }) => {
  // The logo area stays white in both themes so logos keep their own colours.
  const body = (
    <Card className="h-full overflow-hidden">
      <div className="flex items-center justify-center h-32 sm:h-36 bg-white p-4">
        <PartnerLogo partner={partner} />
      </div>
      <div className="border-t border-ink/[0.08] px-4 py-3 text-center">
        <p className="text-sm font-semibold text-ink leading-snug">{partner.name}</p>
        <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-pink-900/50">
          {categoryLabel[partner.category]}
        </p>
      </div>
    </Card>
  );

  return partner.url ? (
    <a href={partner.url} target="_blank" rel="noopener noreferrer" className="group block h-full" aria-label={`${partner.name} (nouvel onglet)`}>
      {body}
    </a>
  ) : (
    <div className="group h-full">{body}</div>
  );
};

const Partners = () => {
  const [partners, setPartners] = useState<Partner[]>(defaultPartners);

  useEffect(() => {
    getPartners()
      .then(list => {
        if (list.length > 0) setPartners(list);
      })
      .catch(err => console.error('Error fetching partners:', err));
  }, []);

  return (
    <div className="relative py-20 md:py-24 overflow-hidden">
      <div className="relative container mx-auto px-4">
        <SectionHeader
          kicker="Ils nous soutiennent"
          title="Nos partenaires"
          lead="Institutions, fondations et commerçants du quartier : sans eux, pas de distribution. Merci à toutes celles et ceux qui donnent, prêtent et cuisinent avec nous."
        />

        <motion.ul
          className="flex flex-wrap justify-center gap-4 md:gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.07 } } }}
        >
          {partners.map(partner => (
            // Flex instead of grid so an incomplete last row stays centred
            <motion.li
              key={partner.id}
              className="w-[calc(50%-0.5rem)] md:w-[calc((100%-3rem)/3)] lg:w-[calc((100%-4.5rem)/4)]"
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
              }}
            >
              <PartnerTile partner={partner} />
            </motion.li>
          ))}
        </motion.ul>

        <div className="panel mt-16 p-8 md:p-10 grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <h3 className="heading text-2xl md:text-3xl mb-4">Devenir partenaire</h3>
            <p className="text-brand-pink-900/75 mb-5">
              Commerce, entreprise, collectivité ou fondation : il y a mille façons de nous aider.
            </p>
            <ul className="grid sm:grid-cols-3 gap-4 text-sm">
              {partnerOffers.map(offer => (
                <li key={offer.title} className="rounded-2xl bg-brand-pink-50 p-4">
                  <p className="font-semibold text-ink mb-1">{offer.title}</p>
                  <p className="text-brand-pink-900/70">{offer.text}</p>
                </li>
              ))}
            </ul>
          </div>
          <a
            href="mailto:nousrire.contact@gmail.com?subject=Partenariat%20avec%20Nous'Rire"
            className="btn-primary justify-self-start md:justify-self-end"
          >
            <EnvelopeIcon className="h-5 w-5" /> Proposer un partenariat
          </a>
        </div>
      </div>
    </div>
  );
};

export default Partners;
